import React, { useRef, useState } from 'react';

import styled from '@emotion/styled';

import TooltipIcon from '../Icons/TooltipIcon';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
interface TooltipProps {
  content: string;
  customIcon?: React.ReactNode;
}
const Tooltip = ({ content, customIcon }: TooltipProps) => {
  const [position, setPosition] = useState<TooltipPosition>('top');
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const calculatePosition = () => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const tooltipWidth = 225;
    const tooltipHeight = 100; // Approximate height

    const spaceTop = rect.top;
    const spaceBottom = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    // Determine best position based on available space
    if (spaceBottom >= tooltipHeight) {
      setPosition('bottom');
    } else if (spaceTop >= tooltipHeight) {
      setPosition('top');
    } else if (spaceRight >= tooltipWidth) {
      setPosition('right');
    } else if (spaceLeft >= tooltipWidth) {
      setPosition('left');
    } else {
      // Default to bottom if no good space
      setPosition('bottom');
    }
  };

  const handleMouseEnter = () => {
    calculatePosition();
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <TooltipContainer
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      position={position}
      isHovered={isHovered}
      content={content}
    >
      {customIcon ? customIcon : <TooltipIcon />}
    </TooltipContainer>
  );
};

export default Tooltip;

const TooltipContainer = styled.span<{
  position: TooltipPosition;
  isHovered: boolean;
  content: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
  cursor: pointer;
  z-index: 5;
  position: relative;
  top: 2px;

  &::before {
    content: '${({ content }) => content || 'help'}';
    position: absolute;
    max-width: 225px;
    min-width: 40px;
    width: max-content;
    background-color: ${({ theme }) => theme.palette.blackColor};
    color: ${({ theme }) => theme.palette.bgWhiteColor};
    padding: 8px;
    border-radius: 8px;
    font-size: 10px;
    white-space: normal;
    opacity: ${({ isHovered }) => (isHovered ? 1 : 0)};
    pointer-events: none;
    transition:
      opacity 0.3s ease-in-out,
      transform 0.3s ease-in-out;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    ${({ position, isHovered }) => {
      switch (position) {
        case 'top':
          return `
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%) ${
              isHovered ? 'translateY(0)' : 'translateY(10px)'
            };
          `;
        case 'bottom':
          return `
            top: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%) ${
              isHovered ? 'translateY(0)' : 'translateY(-10px)'
            };
          `;
        case 'left':
          return `
            right: calc(100% + 8px);
            top: 50%;
            transform: translateY(-50%) ${
              isHovered ? 'translateX(0)' : 'translateX(10px)'
            };
          `;
        case 'right':
          return `
            left: calc(100% + 8px);
            top: 50%;
            transform: translateY(-50%) ${
              isHovered ? 'translateX(0)' : 'translateX(-10px)'
            };
          `;
        default:
          return `
            bottom: calc(100% + 8px);
            left: 50%;
            transform: translateX(-50%) ${
              isHovered ? 'translateY(0)' : 'translateY(10px)'
            };
          `;
      }
    }}
  }
`;
